/**
 * Round Solutions: reusable form drawer.
 *
 * One drawer, several form types. Each type only defines title, fields and subject tag.
 * All submissions go through RSForms.send(), the single place to change how data is sent:
 *   transport "shopify"  -> Shopify contact form (now; goes to the shop email address)
 *   transport "endpoint" -> own endpoint, e.g. a Cloudflare Worker (later; e-mail routing, file upload, Odoo lead)
 */
(() => {
  const CONFIG = {
    transport: 'shopify',
    endpoint: '', // set together with transport: 'endpoint'
    successText: 'Thank you – an engineer will reply within 24 hours.',
    referenceText: 'Your reference number: {ref}',
    privacyText: 'We only use your details to answer this request. See our <a class="link" href="{url}">privacy policy</a>.',
    requiredError: 'Please fill in all required fields.',
    emailError: 'Please enter a valid email address.',
    sendError: 'Sending failed. Please try again in a moment.',
    referenceLabel: 'Reference number'
  };

  // Customer-facing texts follow the page language (<html lang>). The email to the shop stays in
  // English (field labels, form name), so requests always look the same for the team.
  const LANG = (document.documentElement.lang || 'en').toLowerCase().startsWith('de') ? 'de' : 'en';
  if (LANG === 'de') {
    Object.assign(CONFIG, {
      successText: 'Vielen Dank – ein Ingenieur antwortet Ihnen innerhalb von 24 Stunden.',
      referenceText: 'Ihre Referenznummer: {ref}',
      privacyText: 'Wir verwenden Ihre Angaben nur, um diese Anfrage zu beantworten. Siehe unsere <a class="link" href="{url}">Datenschutzerklärung</a>.',
      requiredError: 'Bitte füllen Sie alle Pflichtfelder aus.',
      emailError: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
      sendError: 'Senden fehlgeschlagen. Bitte versuchen Sie es gleich noch einmal.',
      referenceLabel: 'Referenznummer'
    });
  }

  const FIELDS = {
    name: { label: 'Name', de: 'Name', type: 'text', autocomplete: 'name' },
    company: { label: 'Company', de: 'Unternehmen', type: 'text', autocomplete: 'organization' },
    email: { label: 'Business email', de: 'Geschäftliche E-Mail', type: 'email', autocomplete: 'email' },
    part: { label: 'Part number', de: 'Teilenummer', type: 'text' },
    question: { label: 'Your question', de: 'Ihre Frage', type: 'textarea' },
    parts: { label: 'Part numbers and quantities', de: 'Teilenummern und Mengen', type: 'textarea', placeholder: 'LE910C1-EUX × 500\nME910G1-WW × 100' },
    quantity: { label: 'Quantity', de: 'Menge', type: 'text' },
    project: { label: 'Project / application', de: 'Projekt / Anwendung', type: 'textarea' }
  };
  const fieldLabel = (f) => (LANG === 'de' && f.de) || f.label;

  // Same format as the product enquiry forms (custom.js): Q + YYMMDD + 4 random characters.
  function generateReference() {
    const now = new Date();
    const date = String(now.getFullYear()).slice(-2)
      + String(now.getMonth() + 1).padStart(2, '0')
      + String(now.getDate()).padStart(2, '0');
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const random = new Uint8Array(4);
    if (window.crypto && window.crypto.getRandomValues) window.crypto.getRandomValues(random);
    else random.forEach((v, i) => { random[i] = Math.floor(Math.random() * 256); });
    return `Q${date}${Array.from(random, (v) => chars[v % chars.length]).join('')}`;
  }

  // Read-only reference number field, shown to the customer and sent with the request.
  function referenceField(id, value) {
    const div = document.createElement('div');
    div.className = 'form__field';
    div.innerHTML = `<label class="label" for="${id}">${CONFIG.referenceLabel}</label>`
      + `<input class="input w-full" id="${id}" name="reference" type="text" readonly`
      + ' style="background-color: #f8f9fa; cursor: default;">';
    div.querySelector('input').value = value || generateReference();
    return div;
  }

  const TYPES = {
    engineer: {
      tag: 'Engineer',
      title: 'Ask an engineer',
      intro: 'Questions about a part, an integration or the right antenna? An engineer replies within 24 hours.',
      submit: 'Send question',
      de: {
        title: 'Fragen Sie einen Ingenieur',
        intro: 'Fragen zu einem Bauteil, zur Integration oder zur passenden Antenne? Ein Ingenieur antwortet innerhalb von 24 Stunden.',
        submit: 'Frage senden'
      },
      fields: [['name', true], ['company', true], ['email', true], ['part', false], ['question', true]]
    },
    quote: {
      tag: 'Quote',
      title: 'Quick quote',
      intro: 'Price, stock and lead time within 24 hours. No obligation.',
      submit: 'Get my quote within 24 h',
      de: {
        title: 'Schnelles Angebot',
        intro: 'Preis, Lagerbestand und Lieferzeit innerhalb von 24 Stunden. Unverbindlich.',
        submit: 'Angebot innerhalb von 24 h erhalten'
      },
      fields: [['parts', true], ['company', true], ['email', true], ['name', false]]
    },
    sample: {
      tag: 'Sample',
      title: 'Request a sample',
      intro: 'Evaluate before you scale. Tell us what you need for your prototype.',
      submit: 'Request sample',
      de: {
        title: 'Muster anfordern',
        intro: 'Erst testen, dann skalieren. Teilen Sie uns mit, was Sie für Ihren Prototyp benötigen.',
        submit: 'Muster anfordern'
      },
      fields: [['name', true], ['company', true], ['email', true], ['part', true], ['quantity', false], ['project', false]]
    }
  };
  // Customer-facing title, intro and button; def.title stays English for the email and tracking.
  const view = (def) => (LANG === 'de' && def.de) || def;

  const HASH_TO_TYPE = { '#ask-engineer': 'engineer', '#quick-quote': 'quote', '#sample': 'sample' };

  const storage = {
    get(key) { try { return localStorage.getItem(key); } catch (e) { return null; } },
    set(key, value) { try { localStorage.setItem(key, value); } catch (e) { /* ignore */ } }
  };

  // Remember the first UTM source of the visit, so it can be sent with a later request.
  const params = new URLSearchParams(window.location.search);
  if (params.get('utm_source') && !storage.get('rs_utm')) {
    storage.set('rs_utm', ['utm_source', 'utm_medium', 'utm_campaign']
      .map((k) => params.get(k)).filter(Boolean).join(' / '));
  }

  const drawer = document.getElementById('rs-form-drawer');
  if (!drawer) return;

  const el = (sel) => drawer.querySelector(sel);
  const form = el('.js-rs-form');
  let currentType = null;

  function renderType(type, prefill = {}) {
    const def = TYPES[type];
    currentType = type;
    const texts = view(def);
    el('#rs-form-heading').textContent = texts.title;
    el('.js-rs-intro').textContent = texts.intro;
    el('.js-rs-submit').textContent = texts.submit;
    el('.js-rs-privacy').innerHTML = CONFIG.privacyText.replace('{url}', drawer.dataset.privacyUrl);
    el('.js-rs-error').hidden = true;
    el('.js-rs-success').hidden = true;
    form.hidden = false;

    const wrap = el('.js-rs-fields');
    wrap.innerHTML = '';
    wrap.append(referenceField('rs-form-reference', prefill.reference));
    def.fields.forEach(([key, required]) => {
      const f = FIELDS[key];
      const id = `rs-form-${key}`;
      const div = document.createElement('div');
      div.className = 'form__field';
      const label = document.createElement('label');
      label.className = 'label';
      label.htmlFor = id;
      label.innerHTML = fieldLabel(f) + (required ? ' <span class="rs-req">*</span>' : '');
      const input = document.createElement(f.type === 'textarea' ? 'textarea' : 'input');
      if (f.type !== 'textarea') input.type = f.type;
      input.className = 'input w-full';
      input.id = id;
      input.name = key;
      if (f.autocomplete) input.autocomplete = f.autocomplete;
      if (f.placeholder) input.placeholder = f.placeholder;
      if (required) { input.required = true; input.setAttribute('aria-required', 'true'); }
      if (prefill[key]) input.value = prefill[key];
      div.append(label, input);
      wrap.append(div);
    });
  }

  // Shopify only binds its hCaptcha to a contact form once that form gets focus. The transport
  // form is hidden and never focused, so bind it explicitly as soon as someone starts a request.
  // Unbound, Shopify blocks the submit (requestSubmit) or rejects it as missing the captcha (submit).
  function protectTransport() {
    const t = drawer.querySelector('#rs-form-transport');
    const captcha = window.Shopify && window.Shopify.captcha;
    if (!t || t.dataset.cptcha || !captcha || !captcha.protect) return;
    captcha.protect(t);
  }

  function open(type, opener, prefill) {
    if (!TYPES[type]) return;
    protectTransport();
    const values = {
      part: drawer.dataset.defaultPart || '',
      ...prefill
    };
    renderType(type, values);
    const first = form.querySelector('input:not([value]), input[value=""], textarea') || form.querySelector('input');
    drawer.open(opener, first);
  }

  // Report a sent request to Shopify customer events ("rs_lead"). A custom pixel in
  // Settings > Customer events forwards it to GA4 as generate_lead (respects cookie consent).
  function trackLead(type, reference) {
    const publish = () => {
      const analytics = window.Shopify && window.Shopify.analytics;
      if (!analytics || !analytics.publish) return;
      analytics.publish('rs_lead', {
        form_type: type,
        form_name: TYPES[type] ? TYPES[type].title : type,
        reference: reference || '',
        product_title: drawer.dataset.productTitle || '',
        page_url: window.location.origin + window.location.pathname
      });
    };
    if (document.readyState === 'complete') publish();
    else window.addEventListener('load', publish, { once: true });
  }

  function showSuccess(reference) {
    trackLead(currentType, reference);
    form.hidden = true;
    el('.js-rs-intro').textContent = '';
    el('.js-rs-success-text').textContent = CONFIG.successText
      + (reference ? ` ${CONFIG.referenceText.replace('{ref}', reference)}` : '');
    el('.js-rs-success').hidden = false;
  }

  function collect(type, formEl) {
    const def = TYPES[type];
    const data = {};
    def.fields.forEach(([key]) => {
      if (formEl.elements[key]) data[key] = (formEl.elements[key].value || '').trim();
    });
    const reference = (formEl.elements.reference && formEl.elements.reference.value) || generateReference();
    const partRef = data.part || (data.parts ? data.parts.split('\n')[0].trim() : '') || drawer.dataset.productTitle || '';
    const pageUrl = new URL(window.location.href);
    pageUrl.hash = '';
    pageUrl.searchParams.delete('rs_sent');
    pageUrl.searchParams.delete('rs_ref');
    return {
      type,
      reference,
      subject: `${reference} [${def.tag}] ${[partRef, data.company || data.name].filter(Boolean).join(' – ')}`.trim(),
      fields: data,
      meta: {
        pageUrl: pageUrl.href,
        productTitle: drawer.dataset.productTitle || '',
        utmSource: storage.get('rs_utm') || ''
      }
    };
  }

  function validate(type, formEl) {
    const def = TYPES[type];
    for (const [key, required] of def.fields) {
      const input = formEl.elements[key];
      if (!input) continue;
      if (required && !input.value.trim()) return CONFIG.requiredError;
      if (FIELDS[key].type === 'email' && input.value && !input.checkValidity()) return CONFIG.emailError;
    }
    return '';
  }

  // ---- Transports -------------------------------------------------------

  const transports = {
    // Shopify contact form. Limits: goes to the shop email address, fixed subject,
    // no confirmation email, no files. The subject is sent as the first field instead.
    shopify(payload) {
      const t = drawer.querySelector('#rs-form-transport');
      const box = t.querySelector('.js-rs-transport-fields');
      const add = (name, value) => {
        const i = document.createElement('input');
        i.type = 'hidden';
        i.name = name;
        i.value = value;
        box.append(i);
      };
      const f = payload.fields;
      box.innerHTML = '';
      // No "subject" field: Shopify keeps its own email subject ("New customer message on …")
      // and shows such a field with the broken label "New customer message on %{creation_date}".
      add('contact[Reference number]', payload.reference);
      add('contact[Request]', payload.subject);
      add('contact[Form]', TYPES[payload.type].title);
      if (f.name) add('contact[name]', f.name);
      add('contact[email]', f.email || '');
      add('contact[Company]', f.company || '');
      Object.keys(f).forEach((key) => {
        if (['name', 'email', 'company'].includes(key) || !f[key] || !FIELDS[key]) return;
        add(`contact[${FIELDS[key].label}]`, f[key]);
      });
      add('contact[Page URL]', payload.meta.pageUrl);
      if (payload.meta.productTitle) add('contact[Product]', payload.meta.productTitle);
      if (payload.meta.utmSource) add('contact[UTM source]', payload.meta.utmSource);

      const back = new URL(window.location.href);
      back.hash = '';
      back.searchParams.set('rs_sent', payload.type);
      back.searchParams.set('rs_ref', payload.reference);
      t.querySelector('input[name="return_to"]').value = back.pathname + back.search;
      // requestSubmit() fires the submit event, so Shopify's captcha (see protectTransport)
      // can add its token first; a plain submit() skips it.
      protectTransport();
      if (t.requestSubmit) t.requestSubmit(); else t.submit();
      return Promise.resolve({ redirecting: true });
    },

    // Own endpoint (later): JSON, can also carry files and create an Odoo lead.
    endpoint(payload) {
      return fetch(CONFIG.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return { redirecting: false };
      });
    }
  };

  function send(payload) {
    return transports[CONFIG.transport](payload);
  }

  // ---- Events -----------------------------------------------------------

  // Handles the drawer form and inline forms (e.g. Quick quote in the hero, data-rs-inline="quote").
  function handleSubmit(evt, type, formEl, onSuccess) {
    evt.preventDefault();
    if (formEl.elements.website && formEl.elements.website.value) return; // honeypot filled: silently drop

    const error = validate(type, formEl);
    const errorEl = formEl.querySelector('.js-rs-error');
    if (error) {
      errorEl.textContent = error;
      errorEl.hidden = false;
      return;
    }
    errorEl.hidden = true;

    const btn = formEl.querySelector('.js-rs-submit');
    btn.disabled = true;
    const payload = collect(type, formEl);
    send(payload)
      .then((res) => { if (!res.redirecting) onSuccess(payload.reference); })
      .catch(() => {
        errorEl.textContent = CONFIG.sendError;
        errorEl.hidden = false;
      })
      .finally(() => { btn.disabled = false; });
  }

  form.addEventListener('submit', (evt) => handleSubmit(evt, currentType, form, showSuccess));

  document.querySelectorAll('form[data-rs-inline]').forEach((inlineForm) => {
    const type = inlineForm.dataset.rsInline;
    if (!TYPES[type]) return;
    inlineForm.prepend(referenceField(`rs-inline-${type}-reference`));
    inlineForm.addEventListener('focusin', protectTransport, { once: true });
    inlineForm.addEventListener('submit', (evt) => handleSubmit(evt, type, inlineForm, (reference) => {
      renderType(type);
      showSuccess(reference);
      drawer.open();
    }));
  });

  document.addEventListener('click', (evt) => {
    const trigger = evt.target.closest('[data-rs-form], a[href*="#"]');
    if (!trigger) return;
    let type = trigger.dataset.rsForm;
    if (!type && trigger.tagName === 'A') {
      const hash = trigger.getAttribute('href').slice(trigger.getAttribute('href').indexOf('#'));
      type = HASH_TO_TYPE[hash];
    }
    if (!type || !TYPES[type]) return;
    evt.preventDefault();
    const prefill = {};
    if (trigger.dataset.rsPart) prefill.part = trigger.dataset.rsPart;
    open(type, trigger, prefill);
  });

  window.RSForms = { open, send, config: CONFIG, types: TYPES };

  // Back from a Shopify contact submission, or opened via URL hash.
  customElements.whenDefined('side-drawer').then(() => {
    const sent = params.get('rs_sent');
    if (sent && TYPES[sent]) {
      renderType(sent);
      showSuccess(params.get('rs_ref'));
      drawer.open();
      const clean = new URL(window.location.href);
      clean.searchParams.delete('rs_sent');
      clean.searchParams.delete('rs_ref');
      clean.searchParams.delete('contact_posted');
      window.history.replaceState({}, '', clean.pathname + clean.search + clean.hash);
    } else if (HASH_TO_TYPE[window.location.hash]) {
      open(HASH_TO_TYPE[window.location.hash]);
    }
  });
})();
